import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useSearchParams } from 'react-router-dom';
import { CampaignWizard } from '@/features/advertising';
import { useCanAccessAds } from '@/lib/subscription/hooks';
import {
  useCampaignWizardStore,
  type ObjectiveFilter,
} from '@/features/advertising/stores/campaign-wizard.store';
import type { AdType } from '@/features/advertising/types/ads.types';

// Valid ad types that can be passed via URL
const VALID_AD_TYPES: AdType[] = [
  'sponsored_product',
  'homepage_carousel',
  'category_banner',
  'featured_shop',
  'push_shop_subscribers',
  'push_product_likers',
  'push_single_product_likers',
  'push_all_users',
];

export default function AdsCampaignNewPage() {
  const { t } = useTranslation();
  const canAccessAds = useCanAccessAds();
  const [searchParams] = useSearchParams();
  const { initFromParams } = useCampaignWizardStore();

  // Parse URL params
  const typeParam = searchParams.get('type');
  const objectiveParam = searchParams.get('objective') as ObjectiveFilter;

  // Initialize wizard from URL params on mount
  useEffect(() => {
    const validType = typeParam && VALID_AD_TYPES.includes(typeParam as AdType)
      ? (typeParam as AdType)
      : null;

    const validObjective = objectiveParam === 'visibility' || objectiveParam === 'notification'
      ? objectiveParam
      : null;

    initFromParams({ type: validType, objective: validObjective });
  }, [typeParam, objectiveParam, initFromParams]);

  // Redirect to ads overview if user doesn't have ads access
  if (!canAccessAds) {
    return <Navigate to="/ads" replace />;
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h1 className="text-2xl font-bold tracking-tight">{t('ads.campaigns.create', 'Créer une campagne')}</h1>
        <p className="text-muted-foreground">{t('ads.campaigns.createDescription', 'Suivez les étapes pour créer votre campagne publicitaire')}</p>
      </div>
      <CampaignWizard />
    </div>
  );
}
